/**
 * Step 4: Vision & Solution (Consolidated)
 * Combines previous Steps 5 & 6 to show what the community wants to achieve.
 */

import React, { useState } from 'react';
import { Target, Star, Sparkles, TrendingUp, Building2, Plus, Trash2 } from 'lucide-react';
import { StepComponentProps, CommunityAsset } from '../../../../../types/onboarding';
import { InfoTooltip } from '../../../../shared/tooltip/InfoTooltip';
import {
  CURRENT_FEATURE_OPTIONS_WITH_DESCRIPTIONS,
  WISHLIST_FEATURE_OPTIONS_WITH_DESCRIPTIONS,
  GOAL_OPTIONS_WITH_DESCRIPTIONS,
  getTooltipContent,
} from '../../../../../constants/onboarding';

export const Step4VisionSolution: React.FC<StepComponentProps> = ({
  formData,
  errors,
  onFieldChange,
}) => {
  const [customAssets, setCustomAssets] = useState<
    Array<{
      title: string;
      description: string;
      category: CommunityAsset['category'];
    }>
  >([]);

  const handleGoalsChange = (goal: string, checked: boolean) => {
    const current = (formData.goals as string[]) || [];
    if (checked) {
      onFieldChange('goals', [...current, goal]);
    } else {
      onFieldChange(
        'goals',
        current.filter((g) => g !== goal),
      );
    }
  };

  const handleWishlistFeaturesChange = (feature: string, checked: boolean) => {
    const current = (formData.wishlistFeatures as string[]) || [];
    if (checked) {
      onFieldChange('wishlistFeatures', [...current, feature]);
    } else {
      onFieldChange(
        'wishlistFeatures',
        current.filter((f) => f !== feature),
      );
    }
  };

  const handleCommunityAssetsChange = (
    assetName: string,
    category: CommunityAsset['category'],
    isChecked: boolean,
  ) => {
    const currentAssets = (formData.communityAssets as unknown as CommunityAsset[]) || [];
    if (isChecked) {
      const newAsset: CommunityAsset = { name: assetName, category, type: 'predefined' };
      onFieldChange('communityAssets', [...currentAssets, newAsset]);
    } else {
      onFieldChange(
        'communityAssets',
        currentAssets.filter((asset) => asset.name !== assetName),
      );
    }
  };

  const isAssetSelected = (assetName: string): boolean => {
    const currentAssets = (formData.communityAssets as unknown as CommunityAsset[]) || [];
    return currentAssets.some((asset) => asset.name === assetName);
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Target className="w-8 h-8 text-indigo-600" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Vision & Solution Fit</h2>
        <p className="text-lg text-slate-600">
          Define your goals and the features that will help you achieve them
        </p>
      </div>

      {/* Section 1: Primary Goals */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-xs">
        <div className="flex items-center gap-2 mb-4">
          <Star className="w-6 h-6 text-indigo-600" />
          <h3 className="text-xl font-semibold text-slate-800">Strategic Goals</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {GOAL_OPTIONS_WITH_DESCRIPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex items-start p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={(formData.goals as string[])?.includes(option.value) || false}
                onChange={(e) => handleGoalsChange(option.value, e.target.checked)}
                className="mr-3 mt-1 text-indigo-600 focus:ring-indigo-500"
              />
              <div>
                <div className="font-medium text-slate-900 text-sm">{option.value}</div>
                <div className="text-xs text-slate-500 mt-0.5">{option.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Section 2: Feature Wishlist */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-xs">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-6 h-6 text-indigo-600" />
          <h3 className="text-xl font-semibold text-slate-800">Desired Features</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {WISHLIST_FEATURE_OPTIONS_WITH_DESCRIPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex items-start p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={(formData.wishlistFeatures as string[])?.includes(option.value) || false}
                onChange={(e) => handleWishlistFeaturesChange(option.value, e.target.checked)}
                className="mr-3 mt-1 text-indigo-600 focus:ring-indigo-500"
              />
              <div>
                <div className="font-medium text-slate-900 text-sm">{option.value}</div>
                <div className="text-xs text-slate-500 mt-0.5">{option.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Section 3: Assets (Condensed) */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-xs">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="w-6 h-6 text-indigo-600" />
          <h3 className="text-xl font-semibold text-slate-800">Key Community Assets</h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[
            'Common Area Landscaping',
            'Swimming Pool(s)',
            'Security/Gate Systems',
            'Roads/Private Streets',
            'Clubhouse',
            'Fitness Center',
            'Playground(s)',
            'Tennis/Pickleball Courts',
            'Water/Sewer Utilities',
          ].map((asset) => (
            <label key={asset} className="flex items-center p-2 hover:bg-slate-50 rounded">
              <input
                type="checkbox"
                checked={isAssetSelected(asset)}
                onChange={(e) =>
                  handleCommunityAssetsChange(asset, 'Infrastructure Assets', e.target.checked)
                }
                className="mr-2 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-xs text-slate-700">{asset}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-indigo-50 p-4 rounded-lg flex items-start gap-3">
        <TrendingUp className="w-5 h-5 text-indigo-600 mt-0.5" />
        <p className="text-sm text-indigo-800">
          Your goals and feature preferences allow us to customize your demo environment with the
          most relevant data and workflows.
        </p>
      </div>
    </div>
  );
};
